/**
 * SKOP Alerts Page
 *
 * View and manage all price alerts and AI alerts.
 */

import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { AllAlertsList } from '../components/AlertsPanel';
import { AIAlertsPanel } from '../components/AIAlertsPanel';
import { startAlertPolling, stopAlertPolling } from '../lib/alerts';

type AlertTab = 'price' | 'ai';

export function Alerts() {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;
  const [tab, setTab] = useState<AlertTab>('price');

  // Start alert polling
  useEffect(() => {
    if (walletAddress) {
      startAlertPolling(walletAddress);
    }
    return () => {
      stopAlertPolling();
    };
  }, [walletAddress]);

  return (
    <div style={{ paddingTop: '16px', maxWidth: '600px', margin: '0 auto' }}>
      <h1 style={{
        fontSize: '24px',
        fontWeight: 700,
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <span style={{ width: '24px', height: '24px', color: 'var(--accent)' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        </span>
        Alerts
      </h1>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '12px',
      }}>
        <button
          onClick={() => setTab('price')}
          style={{
            padding: '8px 16px',
            background: tab === 'price' ? 'var(--accent-muted)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: tab === 'price' ? 'var(--accent)' : 'var(--text-secondary)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Price Alerts
        </button>
        <button
          onClick={() => setTab('ai')}
          style={{
            padding: '8px 16px',
            background: tab === 'ai' ? 'var(--accent-muted)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: tab === 'ai' ? 'var(--accent)' : 'var(--text-secondary)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          AI Alerts
        </button>
      </div>

      {/* Tab Content */}
      {tab === 'price' && <AllAlertsList />}
      {tab === 'ai' && walletAddress && <AIAlertsPanel walletAddress={walletAddress} compact={false} />}
      {tab === 'ai' && !walletAddress && (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔐</div>
          <p style={{ color: 'var(--text-tertiary)' }}>
            Connect your wallet to view AI alerts
          </p>
        </div>
      )}
    </div>
  );
}
